export const name = "APPENDS";
export const summary = `Returns all rows appended to a table for a given time range.`;
export const reference = `
## APPENDS

Returns all rows appended to a table for a given time range.



### 

\`\`\`
APPENDS(
  TABLE table,
  start_timestamp DEFAULT NULL,
  end_timestamp DEFAULT NULL)
\`\`\`


### Description

The \`APPENDS\` function returns all rows appended to a table for a given time range.

The following operations add rows to the \`APPENDS\` change history:

- \`CREATE TABLE\` DDL statement
- \`INSERT\` DML statement
- Data appended as part of a \`MERGE\` DML statement
- Loading data into BigQuery
- Streaming ingestion


### Definitions

- \`table\`: the BigQuery table name. This must be a regular BigQuery table. This argument must be preceded by the word \`TABLE\`.
- \`start_timestamp\`: a \`TIMESTAMP\`value indicating the earliest time at which a change is included in the output. If the value is \`NULL\`, all changes since the table creation are returned. If the table was created after the\`start_timestamp\` value, the actual table creation time is used instead. An error is returned if the time specified is earlier than allowed by time travel, or if the table was created earlier than allowed by time travel if the\`start_timestamp\` value is \`NULL\`. For standard tables, this window is seven days, but you can configure the time travel window to be less than that.
- \`end_timestamp\`: a \`TIMESTAMP\` value indicating the latest time at which a change is included in the output. \`end_timestamp\` is exclusive; for example, if you specify \`2023-12-31 08:00:00\` for \`start_timestamp\` and\`2023-12-31 12:00:00\` for \`end_timestamp\`, all changes made from 8 AM December 31, 2023 through 11:59 AM December 31, 2023 are returned.


### Details

Records of inserted rows persist even if that data is later deleted. Deletions aren't reflected in the \`APPENDS\` function. If a table is copied, calling the \`APPENDS\` function on the copied table returns every row as inserted at the time of table creation. If a row is modified due to an\`UPDATE\` operation, there's no effect.


### Output

The \`APPENDS\` function returns a table with the following columns:

- All columns of the input table at the time the query is run. If a column is added after the \`end_timestamp\` value, it appears with \`NULL\` values populated in any of the rows that were inserted before the addition of the column.
- \`_CHANGE_TYPE\`: a \`STRING\` value indicating the type of change that produced the row. For \`APPENDS\`, the only supported value is \`INSERT\`.
- \`_CHANGE_TIMESTAMP\`: a \`TIMESTAMP\` value indicating the commit time of the transaction that made the change.


### Limitations

- The data returned by the \`APPENDS\` function is limited to the time travel window of the table.
- The data returned by the \`APPENDS\` function is limited to the table's current schema.
- You can't call the \`APPENDS\` function within a multi-statement transaction.
- You can only use the \`APPENDS\` function with regular BigQuery tables. Clones, snapshots, views, materialized views, external tables, and wildcard tables aren't supported.
- Partition pseudo-columns for ingestion-time partitioned tables, such as\`_PARTITIONTIME\` and \`_PARTITIONDATE\`, aren't included in the function's output.


### Example

This example shows the change history returned by the \`APPENDS\` function as various changes are made to a table called \`Produce\`. First, create the table:



\`\`\`
CREATE TABLE mydataset.Produce (product STRING, inventory INT64) AS (
  SELECT 'apples' AS product, 10 AS inventory);
\`\`\`

Next, insert two rows into the table:



\`\`\`
INSERT INTO mydataset.Produce
VALUES
  ('bananas', 20),
  ('carrots', 30);
\`\`\`

To view the full change history of appends, use \`NULL\` values to get the full history within the time travel window:



\`\`\`
SELECT
  product,
  inventory,
  _CHANGE_TYPE AS change_type,
  _CHANGE_TIMESTAMP AS change_time
FROM
  APPENDS(TABLE mydataset.Produce, NULL, NULL);
\`\`\`

The output is similar to the following:



\`\`\`
+---------+-----------+-------------+--------------------------------+
| product | inventory | change_type | change_time |
+---------+-----------+-------------+--------------------------------+
| apples | 10 | INSERT | 2022-04-15 20:06:00.488000 UTC |
| bananas | 20 | INSERT | 2022-04-15 20:06:08.490000 UTC |
| carrots | 30 | INSERT | 2022-04-15 20:06:08.490000 UTC |
+---------+-----------+-------------+--------------------------------+
\`\`\`

Next, add a column, insert a new row of values, update the inventory, and delete the \`bananas\` row:



\`\`\`
ALTER TABLE mydataset.Produce ADD COLUMN color STRING;
INSERT INTO mydataset.Produce VALUES ('grapes', 40, 'purple');
UPDATE mydataset.Produce SET inventory = inventory + 5 WHERE TRUE;
DELETE mydataset.Produce WHERE product = 'bananas';
\`\`\`

View the new table:



\`\`\`
SELECT * FROM mydataset.Produce;
\`\`\`

The output is similar to the following:



\`\`\`
+---------+-----------+--------+
| product | inventory | color |
+---------+-----------+--------+
| apples | 15 | NULL |
| carrots | 35 | NULL |
| grapes | 45 | purple |
+---------+-----------+--------+
\`\`\`

View the full change history of appends:



\`\`\`
SELECT
  product,
  inventory,
  color,
  _CHANGE_TYPE AS change_type,
  _CHANGE_TIMESTAMP AS change_time
FROM
  APPENDS(TABLE mydataset.Produce, NULL, NULL);
\`\`\`

The output is similar to the following:



\`\`\`
+---------+-----------+--------+-------------+--------------------------------+
| product | inventory | color | change_type | change_time |
+---------+-----------+--------+-------------+--------------------------------+
| apples | 10 | NULL | INSERT | 2022-04-15 20:06:00.488000 UTC |
| bananas | 20 | NULL | INSERT | 2022-04-15 20:06:08.490000 UTC |
| carrots | 30 | NULL | INSERT | 2022-04-15 20:06:08.490000 UTC |
| grapes | 40 | purple | INSERT | 2022-04-15 20:07:45.751000 UTC |
+---------+-----------+--------+-------------+--------------------------------+
\`\`\`

The \`inventory\` column displays the values that were set when the rows were originally inserted into to the table. It doesn't show the changes from the\`UPDATE\` statement. The row with information on bananas is still present because the \`APPENDS\` function only captures additions to tables, not deletions.


source: https://cloud.google.com/bigquery/docs/reference/standard-sql/time-series-functions
`;
